import axios from "axios";
import i18next from "i18next";
import {REQUEST_TIMEOUT, REQUEST_TIMEOUT_REPORTS} from "./constants";
import {trimObjectValues} from "helpers/functions";
import {logOut, updateUserData} from "redux/slices/authSlice";
import {store} from "redux/store";
import {GeneralService} from "services/GeneralService";
import {toast} from "react-toastify";
import {jwtDecode} from "jwt-decode";
import {isMoreThanOneHourPassed} from "../helpers/isMoreThanOneHourPassed";
import getNewTokenAndPermissions from "../helpers/getNewTokenAndPermissions";
import {timeNowInMinutes} from "../helpers/timeNowInMinutes";

let isRefreshing = false; // Tracks if refresh token request is in progress
let refreshQueue = []; // Stores pending requests while refreshing

const WES_URL = process.env.REACT_APP_BASE_URL_API;
const Identity_URL = process.env.REACT_APP_BASE_URL_IDENTITY;

const axiosInstance = axios.create({
    baseURL: WES_URL,
    timeout: REQUEST_TIMEOUT,
});

axiosInstance.interceptors.request.use(
    (config) => {
        const {url, data} = config;
        let trimmedStrings;

        localStorage.setItem("sessionTimeout", timeNowInMinutes());

        // if (isMoreThanOneHourPassed()) {
        //     store.dispatch(logOut());
        //     return simpleErrorHandling(new Error("Session expired. Please log in again."), 'Session expired. Please log in again.');
        // }

        if (typeof data === "object") {
            if (!(data instanceof FormData)) {
                trimmedStrings = trimObjectValues(data);
            } else {
                trimmedStrings = data;
            }
        } else {
            trimmedStrings = data;
        }

        const hasCultureParam = /\bculture=/i.test(url); // Check if 'culture=' parameter already exists

        if (hasCultureParam) {
            return config; // If 'culture=' parameter already exists, return the original config
        } else {
            const cultureParam = `culture=${i18next.language}`;
            const delimiter = url.includes("?") ? "&" : "?"; // Determine whether to use '?' or '&'
            const newUrl = `${url}${delimiter}${cultureParam}`;

            return {
                ...config,
                data: trimmedStrings,
                url: newUrl,
            };
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        // set in the redux store the new accessToken & refreshToken data from success response and recall last API
        if (error?.response?.status === 401) {

            // Prevent looping by checking if this request was already retried
            if (error.config._retry) {
                return Promise.reject(error);
            } else
                error.config._retry = true;

            // check this to remove call of get new access token.
            if (error.config.url.includes("/api/Authentication/GetNewAccessToken")) {
                toast.error("Session expired, please log in again.");
                store.dispatch(logOut());
                return Promise.reject("Session expired, please log in again.");
            }

            // If a refresh request is already in progress, queue the failed requests
            if (isRefreshing) {
                return new Promise((resolve) => {
                    refreshQueue.push((newToken) => {
                        error.config.headers["Authorization"] = `Bearer ${newToken}`;
                        resolve(axiosInstance(error.config));
                    });
                });
            } else
                isRefreshing = true;

            try {
                const res = await getNewTokenAndPermissions()

                isRefreshing = false;
                refreshQueue.forEach((req) => req(res?.accessToken));
                refreshQueue = [];

                error.config.headers.Authorization = `Bearer ${res.accessToken}`;
                return axiosInstance(error.config);
            } catch
                (error) {
                isRefreshing = false;
                refreshQueue = [];

                store.dispatch(logOut());
                return simpleErrorHandling(new Error(error?.message), "Session expired, please log in again.")
            }
        } else if (error?.response?.status === 403) {
            throw new Error(
                error.response.data?.message ||
                "You don't have permission to access this resource"
            );
        } else if (error.message.includes("Session expired")) {
            return Promise.reject(error);
        } else
            return Promise.reject(
                (error.response && error.response.data) ||
                "Unexpected Error, Please Contact Your Administrator."
            );
    }
)
;

//Axios for Identity only
const axios_Identity = axios.create({
    baseURL: Identity_URL,
    timeout: REQUEST_TIMEOUT_REPORTS,
});

axios_Identity.interceptors.request.use(
    (config) => {
        const {url} = config;
        const isCultured = url.includes("?culture=");
        if (isCultured) {
            return config;
        } else {
            if (url.includes("?")) {
                return {
                    ...config,
                    url: url + "&culture=" + i18next.language,
                };
            } else {
                return {...config, url: url + "?culture=" + i18next.language};
            }
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

axios_Identity.interceptors.response.use(
    (response) => response,
    async (error) => {
        const refreshToken = store.getState().auth?.refreshToken;
        const user = store.getState().auth?.user;
        // set in the redux store the new accessToken & refreshToken data from success response and recall last API
        if (error?.response?.status === 401) {
            try {
                const res = await getNewTokenAndPermissions()
                error.config.headers.Authorization = `Bearer ${res.accessToken}`;
                return axios_Identity(error.config);
            } catch (error) {
                store.dispatch(logOut());
            }
        } else if (error?.response?.status === 403) {
            throw new Error(
                error.response.data?.message ||
                "You don't have permission to access this resource"
            );
        } else if (error?.response?.status === 430) {
            store.dispatch(logOut());
            return Promise.reject(new Error(
                error.response.data?.message));
        } else if (error.message.includes("Session expired")) {
            return Promise.reject(error);
        } else
            return Promise.reject(
                (error.response && error.response.data) ||
                "Unexpected Error, Please Contact Your Administrator."
            );
    }
);

const simpleErrorHandling = (err, defaultText = "") => {
    if (err?.message) {
        throw err;
    } else {
        throw new Error(defaultText);
    }
};

export {axios_Identity, simpleErrorHandling};

export default axiosInstance;
